<template>
  <v-container fluid class="pa-4 pa-md-8">
    <!-- Header -->
    <v-row class="mb-4" align="center">
      <v-col cols="12">
        <h2 class="text-h5 font-weight-bold" :style="{ color: colors.primaryVariant }">
          <v-icon left :color="colors.primary">mdi-shield-search</v-icon>
          Registros de Auditoría
        </h2>
        <p class="body-2 mb-0" :style="{ color: colors.diabledText }">
          Actividad registrada en la plataforma visible para administradores
        </p>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card rounded="lg" elevation="0" class="mb-5 pa-4" :style="{ border: `1px solid ${colors.gray}`, background: colors.background }">
      <div class="d-flex align-center justify-space-between mb-3">
        <span class="caption font-weight-bold text-uppercase" :style="{ color: colors.diabledText, letterSpacing: '0.08em' }">
          <v-icon size="14" class="mr-1" :color="colors.diabledText">mdi-filter-outline</v-icon>
          Filtros
        </span>
        <v-btn
          v-if="hasActiveFilters"
          small
          text
          :color="colors.primary"
          @click="clearFilters"
        >
          <v-icon left size="16">mdi-close-circle-outline</v-icon>
          Limpiar
        </v-btn>
      </div>

      <v-row dense align="start">
        <!-- Category filter -->
        <v-col cols="12" sm="6" md="3">
          <div class="caption mb-1" :style="{ color: colors.diabledText }">Categoría</div>
          <v-select
            v-model="filters.category"
            :items="categoryOptions"
            placeholder="Todas"
            clearable
            dense
            outlined
            hide-details
            @change="onFilterChange"
          />
        </v-col>

        <!-- Status filter -->
        <v-col cols="12" sm="6" md="2">
          <div class="caption mb-1" :style="{ color: colors.diabledText }">Estado</div>
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            placeholder="Todos"
            clearable
            dense
            outlined
            hide-details
            @change="onFilterChange"
          />
        </v-col>

        <!-- Start date -->
        <v-col cols="12" sm="6" md="2">
          <div class="caption mb-1" :style="{ color: colors.diabledText }">Desde</div>
          <v-menu
            v-model="dateMenuStart"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="filters.startDate"
                placeholder="dd/mm/aaaa"
                readonly
                dense
                outlined
                hide-details
                clearable
                append-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
                @click:clear="clearStartDate"
              />
            </template>
            <v-date-picker
              v-model="filters.startDate"
              :max="filters.endDate || undefined"
              locale="es"
              :color="colors.primary"
              @change="dateMenuStart = false; onFilterChange()"
            />
          </v-menu>
        </v-col>

        <!-- End date -->
        <v-col cols="12" sm="6" md="2">
          <div class="caption mb-1" :style="{ color: colors.diabledText }">Hasta</div>
          <v-menu
            v-model="dateMenuEnd"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="filters.endDate"
                placeholder="dd/mm/aaaa"
                readonly
                dense
                outlined
                hide-details
                clearable
                append-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
                @click:clear="clearEndDate"
              />
            </template>
            <v-date-picker
              v-model="filters.endDate"
              :min="filters.startDate || undefined"
              locale="es"
              :color="colors.primary"
              @change="dateMenuEnd = false; onFilterChange()"
            />
          </v-menu>
        </v-col>

        <!-- Items per page -->
        <v-col cols="12" sm="6" md="2">
          <div class="caption mb-1" :style="{ color: colors.diabledText }">Por página</div>
          <v-select
            v-model="filters.limit"
            :items="limitOptions"
            dense
            outlined
            hide-details
            @change="onFilterChange"
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Loading state -->
    <v-row v-if="loading" justify="center" class="py-10">
      <v-progress-circular indeterminate :color="colors.primary" />
    </v-row>

    <!-- Empty state -->
    <v-card
      v-else-if="!loading && filteredDocs.length === 0"
      rounded="lg"
      elevation="0"
      class="pa-8 text-center"
      :style="{ border: `1px solid ${colors.gray}` }"
    >
      <v-icon size="56" :color="colors.gray">mdi-shield-search</v-icon>
      <p class="title mb-1 mt-4" :style="{ color: colors.primaryVariant }">Sin registros</p>
      <p class="body-2 mb-0" :style="{ color: colors.diabledText }">
        No hay registros de auditoría que coincidan con los filtros seleccionados.
      </p>
    </v-card>

    <!-- Logs list -->
    <template v-else>
      <v-card
        v-for="log in filteredDocs"
        :key="log._id"
        rounded="lg"
        elevation="0"
        class="mb-2"
        :style="{ border: `1px solid ${colors.gray}` }"
      >
        <!-- Row summary -->
        <v-expansion-panels flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="py-3 px-4" style="min-height: unset;">
              <div class="d-flex align-center flex-wrap" style="gap: 8px; min-width: 0;">
                <!-- Status badge -->
                <span
                  class="caption font-weight-bold px-2 py-1 rounded"
                  :style="statusStyle(log.status)"
                >{{ statusLabel(log.status) }}</span>

                <!-- Category badge -->
                <span
                  class="caption px-2 py-1 rounded"
                  :style="{ background: colors.primaryVariantClear, color: colors.primary }"
                >{{ categoryLabel(log.category) }}</span>

                <!-- Description -->
                <span class="body-2 text-truncate" :style="{ color: colors.primaryVariant, minWidth: 0 }">
                  {{ log.description || actionLabel(log.action) }}
                </span>
              </div>

              <!-- Meta info right-aligned -->
              <template #actions>
                <div class="d-flex align-center ml-4" style="gap: 12px; flex-shrink: 0; white-space: nowrap;">
                  <span class="caption" :style="{ color: colors.diabledText }">
                    <v-icon size="12" class="mr-1" :color="colors.diabledText">mdi-account-outline</v-icon>
                    {{ log.actorEmail || 'Sistema' }}
                  </span>
                  <span class="caption" :style="{ color: colors.diabledText }">
                    {{ formatDate(log.createdAt) }}
                  </span>
                  <v-icon :color="colors.diabledText">$expand</v-icon>
                </div>
              </template>
            </v-expansion-panel-header>

            <!-- Expanded detail -->
            <v-expansion-panel-content>
              <v-divider :style="{ borderColor: colors.gray }" />
              <div class="pa-4" :style="{ background: colors.background }">
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <div class="caption font-weight-bold text-uppercase mb-1" :style="{ color: colors.diabledText }">Acción</div>
                    <div class="body-2 font-weight-medium" :style="{ color: colors.primaryVariant }">{{ actionLabel(log.action) }}</div>
                  </v-col>
                  <v-col v-if="log.actorRole" cols="12" sm="6" md="3">
                    <div class="caption font-weight-bold text-uppercase mb-1" :style="{ color: colors.diabledText }">Rol del actor</div>
                    <div class="body-2" :style="{ color: colors.primaryVariant }">{{ roleLabel(log.actorRole) }}</div>
                  </v-col>
                  <v-col v-if="log.targetResource" cols="12" sm="6" md="3">
                    <div class="caption font-weight-bold text-uppercase mb-1" :style="{ color: colors.diabledText }">Recurso</div>
                    <div class="body-2" :style="{ color: colors.primaryVariant }">{{ log.targetResource }}</div>
                  </v-col>
                  <v-col v-if="log.targetId" cols="12" sm="6" md="3">
                    <div class="caption font-weight-bold text-uppercase mb-1" :style="{ color: colors.diabledText }">ID Recurso</div>
                    <div class="body-2 text-truncate" style="max-width: 220px;" :title="String(log.targetId)" :style="{ color: colors.primaryVariant }">
                      {{ log.targetId }}
                    </div>
                  </v-col>
                  <v-col v-if="log.ipAddress" cols="12" sm="6" md="3">
                    <div class="caption font-weight-bold text-uppercase mb-1" :style="{ color: colors.diabledText }">Dirección IP</div>
                    <div class="body-2" :style="{ color: colors.primaryVariant }">{{ log.ipAddress }}</div>
                  </v-col>
                  <v-col v-if="log.errorMessage" cols="12">
                    <div class="caption font-weight-bold text-uppercase mb-1" :style="{ color: colors.diabledText }">Mensaje de error</div>
                    <div class="body-2" :style="{ color: colors.contrastError }">{{ log.errorMessage }}</div>
                  </v-col>
                  <v-col v-if="log.metadata && hasMetadata(log.metadata)" cols="12">
                    <div class="caption font-weight-bold text-uppercase mb-1" :style="{ color: colors.diabledText }">Metadata</div>
                    <div class="pa-3 rounded" :style="{ border: `1px solid ${colors.gray}`, background: colors.surface }">
                      <pre style="white-space: pre-wrap; word-break: break-word; font-size: 12px; margin: 0;" :style="{ color: colors.primaryVariant }">{{ formatMetadata(log.metadata) }}</pre>
                    </div>
                  </v-col>
                  <v-col v-if="log.userAgent" cols="12">
                    <div class="caption font-weight-bold text-uppercase mb-1" :style="{ color: colors.diabledText }">User Agent</div>
                    <div class="caption text-truncate" :title="log.userAgent" :style="{ color: colors.diabledText }">{{ log.userAgent }}</div>
                  </v-col>
                </v-row>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>

      <!-- Pagination -->
      <v-row justify="center" align="center" class="mt-6">
        <v-col cols="12" class="d-flex flex-column align-center" style="gap: 8px;">
          <v-pagination
            v-model="filters.page"
            :length="auditLogsPaginated.totalPages || 1"
            :total-visible="10"
            :color="colors.primary"
            @input="fetchLogs"
          />
          <span class="caption" :style="{ color: colors.diabledText }">
            {{ auditLogsPaginated.totalDocs || 0 }} registros totales
          </span>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { colors } from '@/constants/colors';

const HIDDEN_CATEGORIES = new Set(['MEMBERSHIP', 'TICKET', 'INVOICE', 'ACCOUNT', 'HELPDESK', 'ZOOM', 'SYSTEM', 'MOVEMENT', 'ITEM']);

const CATEGORY_LABELS = {
  AUTH:        'Autenticación',
  USER:        'Usuarios',
  EVENT:       'Eventos',
  ORDER:       'Órdenes',
  PAYMENT:     'Pagos',
  MEMBER:      'Miembros',
  AFFILIATION: 'Membresías',
  ATTENDEE:    'Asistentes',
  BILLING:     'Facturación',
  COUPON:      'Cupones',
  PUBLICATION: 'Publicaciones',
  COMBO:       'Combos',
};

const ACTION_LABELS = {
  AUTH_LOGIN:                    'Inicio de sesión',
  AUTH_LOGIN_FAILED:             'Intento de inicio de sesión fallido',
  AUTH_TOKEN_REFRESH:            'Renovación de sesión',
  AUTH_PASSWORD_RECOVER_REQUEST: 'Solicitud de recuperación de contraseña',
  AUTH_PASSWORD_RESET:           'Restablecimiento de contraseña',
  AUTH_MEMBER_PASSWORD_RESET:    'Restablecimiento de contraseña de miembro',
  USER_CREATE:                   'Creación de usuario',
  USER_UPDATE:                   'Actualización de usuario',
  USER_PASSWORD_CHANGE:          'Cambio de contraseña',
  USER_PROFILE_PICTURE_UPDATE:   'Actualización de foto de perfil',
  USER_EMAIL_CONFIRM:            'Confirmación de correo electrónico',
  USER_VALIDATE:                 'Validación de usuario',
  USER_STATUS_CHANGE:            'Cambio de estatus de usuario',
  USER_PERMISSIONS_UPDATE:       'Actualización de permisos',
  USER_MEMBER_CREATE:            'Creación de usuario miembro',
  USER_MIGRATION:                'Migración de usuario',
  EVENT_CREATE:                  'Creación de evento',
  EVENT_UPDATE:                  'Actualización de evento',
  EVENT_DELETE:                  'Eliminación de evento',
  EVENT_PUBLISH:                 'Publicación de evento',
  EVENT_HIDE:                    'Evento ocultado',
  EVENT_CLONE:                   'Clonación de evento',
  EVENT_EXPIRE:                  'Expiración de evento',
  EVENT_SPONSOR_ADD:             'Patrocinador agregado',
  EVENT_SPONSOR_REMOVE:          'Patrocinador eliminado',
  EVENT_DIPLOMA_SEND:            'Envío de diploma',
  EVENT_REMINDER_SET:            'Recordatorio configurado',
  EVENT_REMINDER_DELETE:         'Recordatorio eliminado',
  EVENT_REMINDER_EMAIL_SEND:     'Correo recordatorio enviado',
  EVENT_POINTS_SET:              'Puntos configurados',
  EVENT_POINTS_DELETE:           'Puntos eliminados',
  ORDER_CREATE:                  'Creación de orden',
  ORDER_UPDATE:                  'Actualización de orden',
  ORDER_STATUS_CHANGE:           'Cambio de estatus de orden',
  ORDER_PAYMENT_REF_SET:         'Referencia de pago asignada',
  PAYMENT_CARD_INTENT_CREATE:    'Inicio de pago con tarjeta',
  PAYMENT_CARD_CONFIRM:          'Pago con tarjeta confirmado',
  PAYMENT_CASH_INTENT_CREATE:    'Inicio de pago en efectivo',
  PAYMENT_BANK_TRANSFER_CREATE:  'Transferencia bancaria creada',
  PAYMENT_ORDER_CONFIRM_PAID:    'Pago de orden confirmado',
  PAYMENT_ORDER_CONFIRM_FREE:    'Orden gratuita confirmada',
  PAYMENT_PEIBO_INTENT_CREATE:   'Inicio de pago PEIBO',
  PAYMENT_PEIBO_CONFIRM:         'Pago PEIBO confirmado',
  PAYMENT_REFERENCE_SEND:        'Referencia de pago enviada',
  MEMBER_CREATE:                 'Creación de miembro',
  MEMBER_EXPORT:                 'Exportación de miembros',
  MEMBER_IMPORT:                 'Importación de miembros',
  AFFILIATION_CREATE:            'Creación de membresía',
  AFFILIATION_UPDATE:            'Actualización de membresía',
  AFFILIATION_DELETE:            'Eliminación de membresía',
  AFFILIATION_TYPE_CREATE:       'Creación de tipo de membresía',
  AFFILIATION_TYPE_UPDATE:       'Actualización de tipo de membresía',
  AFFILIATION_TYPE_DELETE:       'Eliminación de tipo de membresía',
  AFFILIATE_CREATE:              'Suscripción creada',
  AFFILIATE_UPDATE:              'Suscripción actualizada',
  ATTENDEE_CHECKIN:              'Registro de entrada (check-in)',
  ATTENDEE_CHECKOUT:             'Registro de salida (check-out)',
  ATTENDEE_SESSION_CREATE:       'Sesión de asistente creada',
  BILLING_UPDATE:                'Actualización de datos de facturación',
  COUPON_CREATE:                 'Creación de cupón',
  COUPON_UPDATE:                 'Actualización de cupón',
  COUPON_DELETE:                 'Eliminación de cupón',
  COUPON_APPLY:                  'Cupón aplicado',
  PUBLICATION_CREATE:            'Creación de publicación',
  PUBLICATION_UPDATE:            'Actualización de publicación',
  PUBLICATION_DELETE:            'Eliminación de publicación',
  COMBO_CREATE:                  'Creación de combo',
  COMBO_UPDATE:                  'Actualización de combo',
  COMBO_DELETE:                  'Eliminación de combo',
};

const ROLE_LABELS = {
  HEAD_OFFICE:       'Administrador',
  ORGANIZATION:      'Organización',
  ORGANIZATION_USER: 'Usuario de organización',
  BRANCH:            'Sucursal',
  MEMBER:            'Miembro',
  SYSTEM:            'Sistema',
};

export default {
  name: 'AuditLogs',

  data() {
    return {
      colors,
      loading: false,
      dateMenuStart: false,
      dateMenuEnd: false,
      filters: {
        page: 1,
        limit: 20,
        category: null,
        status: null,
        startDate: null,
        endDate: null
      },
      statusOptions: [
        { text: 'Exitoso', value: 'SUCCESS' },
        { text: 'Fallido', value: 'FAILURE' },
        { text: 'Error', value: 'ERROR' }
      ],
      categoryOptions: Object.entries(CATEGORY_LABELS).map(([value, text]) => ({ text, value })),
      limitOptions: [
        { text: '10', value: 10 },
        { text: '20', value: 20 },
        { text: '50', value: 50 },
        { text: '100', value: 100 }
      ]
    };
  },

  computed: {
    ...mapState('auditLogs', ['auditLogsPaginated']),

    filteredDocs() {
      return this.auditLogsPaginated.docs.filter(log => !HIDDEN_CATEGORIES.has(log.category));
    },

    hasActiveFilters() {
      return !!(this.filters.category || this.filters.status || this.filters.startDate || this.filters.endDate);
    }
  },

  mounted() {
    this.fetchLogs();
  },

  methods: {
    ...mapActions('auditLogs', ['getAuditLogs']),

    async fetchLogs() {
      this.loading = true;
      await this.getAuditLogs({
        page: this.filters.page,
        limit: this.filters.limit,
        category: this.filters.category || '',
        status: this.filters.status || '',
        startDate: this.filters.startDate || '',
        endDate: this.filters.endDate || ''
      });
      this.loading = false;
    },

    onFilterChange() {
      this.filters.page = 1;
      this.fetchLogs();
    },

    clearFilters() {
      this.filters = { page: 1, limit: 20, category: null, status: null, startDate: null, endDate: null };
      this.fetchLogs();
    },

    clearStartDate() {
      this.filters.startDate = null;
      this.onFilterChange();
    },

    clearEndDate() {
      this.filters.endDate = null;
      this.onFilterChange();
    },

    statusStyle(status) {
      const { active, secondary, error, contrastError, primaryVariant } = this.colors;
      const map = {
        SUCCESS: { background: active, color: primaryVariant },
        FAILURE: { background: secondary, color: primaryVariant },
        ERROR:   { background: error,   color: contrastError }
      };
      return map[status] || { background: this.colors.gray, color: primaryVariant };
    },

    statusLabel(status) {
      const map = { SUCCESS: 'Exitoso', FAILURE: 'Fallido', ERROR: 'Error' };
      return map[status] || status;
    },

    categoryLabel(category) {
      return CATEGORY_LABELS[category] || category;
    },

    actionLabel(action) {
      return ACTION_LABELS[action] || action;
    },

    roleLabel(role) {
      return ROLE_LABELS[role] || role;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleString('es-MX', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },

    hasMetadata(metadata) {
      return metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0;
    },

    formatMetadata(metadata) {
      try {
        return JSON.stringify(metadata, null, 2);
      } catch {
        return String(metadata);
      }
    }
  }
};
</script>
